import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const leaseFinanceDifferences = [
    {
        id: 'lease-better',
        title: '1. When Leasing Makes More Sense',
        text: 'Leasing works better for drivers who want predictable short-term costs and newer cars every few years. It usually offers lower monthly payments and avoids long-term ownership risks, provided you stay within mileage limits.'
    },
    {
        id: 'finance-better',
        title: '2. When Financing Makes More Sense',
        text: 'Financing is the better fit if you plan to keep the car for many years and want full ownership. You build equity with each payment and eventually own the asset free and clear, with no restrictions on mileage or modifications.'
    },
    {
        id: 'costs',
        title: '3. What the Real Cost Difference Looks Like',
        text: 'While leasing looks cheaper month-to-month, financing often wins on total cost over 5-10 years. A financed car eventually has no monthly payment, whereas a serial leaser is always making payments and never builds equity.'
    },
    {
        id: 'decision',
        title: '4. A Simple Way to Decide',
        text: 'Match the choice to your lifestyle: Choose leasing if you value flexibility and a new car every 3 years. Choose financing if you value long-term stability, high mileage capacity, and eventual freedom from car payments.'
    }
];

const faqs = [
    {
        question: 'Is leasing always cheaper than financing?',
        answer: 'Monthly lease payments are usually lower because you only pay for the car’s depreciation. However, total long-term costs are often lower with financing if you keep the car after paying it off.'
    },
    {
        question: 'Does financing make more sense if I keep cars for a long time?',
        answer: 'Yes. Once the loan is paid off, your only costs are maintenance/insurance. This "equity period" is where financing provides the most financial benefit compared to leasing.'
    },
    {
        question: 'Is leasing a bad idea if I drive a lot every year?',
        answer: 'It can be. Most leases have mileage caps (e.g., 10k-15k miles). Exceeding these limits can lead to heavy per-mile penalties at the end of the term.'
    },
    {
        question: 'What should matter most when choosing?',
        answer: 'Your ownership horizon (how long you keep cars), your average annual mileage, and whether you prefer lower short-term payments or long-term asset value.'
    }
];

const Blog16 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 16;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            Lease vs Finance a Car: Which Option Is Better in 2026?
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Choosing between a lease and financing isn't just about the monthly payment—it's about how you plan to use the car long-term.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 8 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/16_1.jpeg"
                            alt="Lease versus finance car decision in 2026"
                            title="Lease vs Finance a Car: Which Option Is Better in 2026?"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: buying_vs_leasing_decision.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#lease-better" className="text-gray-900 no-underline hover:underline">When leasing makes sense</a></li>
                            <li><a href="#finance-better" className="text-gray-900 no-underline hover:underline">When financing makes sense</a></li>
                            <li><a href="#costs" className="text-gray-900 no-underline hover:underline">Real cost difference</a></li>
                            <li><a href="#decision" className="text-gray-900 no-underline hover:underline">How to decide</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            A lot of drivers focus on the monthly payment first. That is usually where the confusion starts. A lease can look cheaper up front, while financing can cost more month to month but give you long-term value that a lease never does.
                        </p>
                        <p className="mb-0 text-gray-700">
                            The better option depends on how long you plan to keep the car, how much you drive, and whether lower monthly cost or long-term ownership matters more to you.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>Lease or Finance?</strong><br />
                            Leasing is generally better for shorter commitments (2-4 years) and lower payments. Financing is the smarter financial move if you keep your car for 5+ years and want to build ownership equity.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/16_2.jpeg"
                            alt="Driver comparing lease and finance options at a dealership"
                            title="Comparing car ownership options"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: ownership_comparison.png
                        </figcaption>
                    </figure>

                    {leaseFinanceDifferences.map((item) => (
                        <section key={item.id} id={item.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {item.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{item.text}</p>
                        </section>
                    ))}

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Deciding how to pay for your car is a big financial step. CarFido Assistant helps you compare the full picture of ownership costs.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Compare the total cost of ownership between lease and finance options</li>
                                <li>Get guidance based on your predicted annual mileage and lifestyle</li>
                                <li>Store your purchase and financing documents in a secure digital glove box</li>
                                <li>Understand long-term value impact beyond just the monthly payment</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Lease vs finance is not about which option is better for everyone—it's about which one fits your budget and goals.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Drivers who want flexibility and newer cars often lean toward leasing. Those who want long-term value and freedom from car payments usually do better with financing. Compare the full picture, and the right choice will become clear.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#lease-better" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Leasing</a></li>
                            <li><a href="#finance-better" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Financing</a></li>
                            <li><a href="#costs" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Costs</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog16;
